// BIND 튜토리얼 — 스텝 루프 영상은 화면 안에서만 재생(사용자가 멈춘 영상은 다시 켜지 않음),
// 모션 줄이기면 자동재생하지 않고 토글로만 재생. 진행 바는 재생 위치를 따라가고 드래그로 이동한다.
// 인디케이터 점은 스크롤 위치를 따라가고, 누르면 그 스텝으로 이동한다.
// BIND tutorial — step loops play only while on screen (a video the viewer paused stays paused);
// with reduced motion nothing autoplays and the toggle is the only way to play. Progress bars
// follow playback and can be dragged to seek. Indicator dots follow the scroll and jump to their step.
class BindLoops extends HTMLElement {
  connectedCallback() {
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.initDots(reduceMotion);

    const videos = this.querySelectorAll('video');
    videos.forEach((video) => this.initProgress(video));

    // 토글 클릭은 global.js 의 document 핸들러보다 먼저 여기 도착 — 뒤집히기 전 상태로 기록
    // a toggle click reaches this element before global.js's document handler flips the video
    this.addEventListener('click', (event) => {
      const video = event.target.closest('.product__video-toggle')?.parentElement.querySelector('video');
      if (video) video.dataset.userPaused = String(!video.paused);
    });

    if (reduceMotion) return;
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) target.pause();
        else if (target.dataset.userPaused !== 'true') target.play().catch(() => {});
      });
    }, { threshold: 0.25 });
    videos.forEach((video) => this.observer.observe(video));
  }

  // 재생 중에만 매 프레임 갱신, 멈추면 그 위치에 머문다. 드래그하면 그 위치로 이동.
  // updates each frame only while playing; dragging seeks to that point.
  initProgress(video) {
    const bar = video.parentElement.querySelector('.bind-way__progress');
    if (!bar) return;
    const paint = (percent) => bar.style.setProperty('--p', percent / 100);
    let frame;
    const tick = () => {
      // 누르고 있는 동안은 손가락 위치가 우선 — while pressed, the finger wins
      if (video.duration && !bar.matches(':active')) {
        const percent = (video.currentTime / video.duration) * 100;
        bar.value = percent;
        paint(percent);
      }
      frame = requestAnimationFrame(tick);
    };
    bar.addEventListener('input', () => {
      paint(bar.value);
      if (video.duration) video.currentTime = (bar.value / 100) * video.duration;
    });
    video.addEventListener('play', () => { cancelAnimationFrame(frame); tick(); });
    video.addEventListener('pause', () => cancelAnimationFrame(frame));
  }

  // 점은 슬라이더 바로 다음 형제 — the dots are the slider's next sibling
  initDots(reduceMotion) {
    const dots = this.nextElementSibling?.querySelectorAll('.bind-way__dot');
    if (!dots?.length) return;
    const steps = [...this.children];
    const offset = (i) => steps[i].offsetLeft - steps[0].offsetLeft;

    dots.forEach((dot, i) => dot.addEventListener('click', () => {
      this.scrollTo({ left: offset(i), behavior: reduceMotion ? 'auto' : 'smooth' });
    }));

    this.addEventListener('scroll', () => {
      // 끝까지 밀면 마지막 스텝은 왼쪽에 닿지 못하므로 마지막 점을 켠다
      // at the scroll end the last step can't reach the left edge, so light the last dot
      const atEnd = this.scrollLeft >= this.scrollWidth - this.clientWidth - 1;
      let active = atEnd ? steps.length - 1 : 0;
      if (!atEnd) steps.forEach((_, i) => { if (this.scrollLeft >= offset(i) - offset(1) / 2) active = i; });
      dots.forEach((dot, i) => {
        if (i === active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }, { passive: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('bind-loops')) customElements.define('bind-loops', BindLoops);
